import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Fuel,
  Receipt,
  FileCheck,
  ChevronRight,
  PhoneCall,
  Zap,
} from "lucide-react";

const TripActionsHub = () => {
  const navigate = useNavigate();

const actionCards = [
    {
      id: "refuel",
      title: "Refuel & History",
      description:
        "Log your vehicle's fuel details and review your past refueling history.",
      path: "/refuel",
      icon: <Fuel size={28} color="white" />,
      // Beautiful modern gradients using inline styles
      gradient: "linear-gradient(135deg, #0d6efd 0%, #0dcaf0 100%)",
    },
    {
      id: "expense",
      title: "Expenses & History",
      description: "Record new tolls or parking fees and track your past expense logs.",
      path: "/other-expense",
      icon: <Receipt size={28} color="white" />,
      gradient: "linear-gradient(135deg, #6f42c1 0%, #d63384 100%)",
    },
    {
      id: "dmr",
      title: "DMR & History",
      description:
        "Generate your Daily Movement Register and access your previous trip records.",
      path: "/dmr",
      icon: <FileCheck size={28} color="white" />,
      gradient: "linear-gradient(135deg, #198754 0%, #20c997 100%)",
    },
    {
      id: "contact",
      title: "Contact Office",
      description:
        "Facing any errors in the app or issues with your trip? Tap here to reach support.",
      path: "/contact-office",
      icon: <PhoneCall size={28} color="white" />,
      gradient: "linear-gradient(135deg, #fd7e14 0%, #dc3545 100%)", // Orange to Red for Help/Support
    },
  ];

  // Framer Motion Animation Variants for smooth loading
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 120, damping: 14 },
    },
  };

  return (
    <div
      className="min-vh-100 d-flex flex-column align-items-center pt-2 pb-2 px-3"
      style={{ backgroundColor: "#ffffff" }}
    >
      {/* Header Section */}
      {/* Header Section */}
      <div className="w-100 mb-4 mt-2" style={{ maxWidth: "500px" }}>
        {/* 2. The Main Title (with sleek letter spacing) */}
        <h2
          className="text-primary mb-2"
          style={{
            fontWeight: "900",
            letterSpacing: "-0.5px",
            fontSize: "1.5rem",
          }}
        >
          Quick Action
        </h2>

        {/* 3. The Professional Subtitle */}
        <p
          className="text-secondary mb-0"
          style={{ fontSize: "0.95rem", lineHeight: "1.5", opacity: "0.85" }}
        >
          Manage your trip logs, record expenses, or connect with our support
          team seamlessly.
        </p>
      </div>

      {/* Animated Cards Container */}
      <motion.div
        className="w-100"
        style={{ maxWidth: "500px" }}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {actionCards.map((card) => (
          <motion.div
            key={card.id}
            variants={cardVariants}
            whileHover={{ scale: 1.02, translateY: -3 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(card.path)}
            // Bootstrap Card classes for structure and shadow
            className="card border-0 shadow-sm mb-3 rounded-4"
            style={{ cursor: "pointer" }}
          >
            <div className="card-body p-4 d-flex align-items-center">
              {/* Colorful Gradient Icon Box */}
              <div
                className="d-flex justify-content-center align-items-center rounded-3 me-3 shadow-sm"
                style={{
                  minWidth: "60px",
                  height: "60px",
                  background: card.gradient,
                }}
              >
                {card.icon}
              </div>

              {/* Text Content */}
              <div className="flex-grow-1">
                <h5
                  className="fw-bold text-dark mb-1"
                  style={{ fontSize: "1.15rem" }}
                >
                  {card.title}
                </h5>
                <p
                  className="text-secondary mb-0"
                  style={{ fontSize: "0.85rem", lineHeight: "1.4" }}
                >
                  {card.description}
                </p>
              </div>

              {/* Arrow Indicator */}
              <div className="text-secondary ms-2">
                <ChevronRight size={24} />
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default TripActionsHub;